import React, { useState } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';

export default function SubscriptionPage() {
  const [currentPlan, setCurrentPlan] = useState('free'); // 'free' or 'basic_paid'
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Mock user data
  const userData = {
    name: 'The Coffee House',
    email: 'sarah@thecoffeehouse.com',
    planSince: 'January 2025',
    eventsUsed: 3,
    eventsLimit: currentPlan === 'free' ? 4 : 999,
    aiUsed: 2,
    aiLimit: currentPlan === 'free' ? 5 : 999,
    nextBilling: currentPlan === 'basic_paid' ? 'February 20, 2025' : null,
    amount: currentPlan === 'basic_paid' ? 20 : 0
  };

  const plans = {
    free: {
      name: 'Free Tier',
      price: 0,
      period: 'forever',
      description: 'Perfect for trying out TrentConnect',
      features: [
        '1 event per month',
        'Basic event pages',
        '3 AI descriptions per month',
        'Platform discovery',
        'Community support'
      ],
      limitations: [
        'No RSVP data storage',
        'No customer management',
        'No email campaigns',
        'No analytics dashboard',
        'No event editing'
      ]
    },
    basic_paid: {
      name: 'Basic Paid',
      price: 20,
      period: 'month',
      description: 'Everything you need to grow your business',
      features: [
        'Unlimited events',
        'Professional event pages',
        'Unlimited AI descriptions',
        'Priority discovery placement',
        'Full RSVP management',
        'Customer database',
        'Email campaigns',
        'Advanced analytics',
        'Full event editing',
        'Priority email support',
        'Export customer data',
        'Social media integration'
      ],
      limitations: []
    }
  };

  const handleUpgrade = async () => {
    try {
      setIsUpgrading(true);
      // Simulate upgrade process
      await new Promise(resolve => setTimeout(resolve, 2000));
      setCurrentPlan('basic_paid');
      setShowUpgradeModal(false);
      alert('Successfully upgraded to Basic Paid! 🎉');
    } catch (error) {
      alert('Upgrade failed. Please try again.');
    } finally {
      setIsUpgrading(false);
    }
  };

  const handleCancel = async () => {
    try {
      setIsCancelling(true);
      // Simulate cancellation process
      await new Promise(resolve => setTimeout(resolve, 1500));
      setCurrentPlan('free');
      setShowCancelModal(false);
      alert('Subscription cancelled. You\'ll keep premium features until February 20, 2025.');
    } catch (error) {
      alert('Cancellation failed. Please try again.');
    } finally {
      setIsCancelling(false);
    }
  };

  const getUsagePercentage = (used, limit) => {
    if (limit === 999) return 0; // Unlimited
    return (used / limit) * 100;
  };

  return (
    <DashboardLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Subscription & Pricing</h1>
          <p className="text-slate-600">
            Manage your plan and unlock powerful features to grow your business
          </p>
        </div>

        {/* Current Plan Status */}
        <div className={`mb-8 p-6 rounded-xl border ${
          currentPlan === 'basic_paid' 
            ? 'bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200' 
            : 'bg-white border-slate-200'
        } shadow-sm`}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center space-x-4 mb-6 lg:mb-0">
              <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${
                currentPlan === 'basic_paid' ? 'bg-gradient-to-r from-purple-600 to-blue-600' : 'bg-slate-600'
              }`}>
                <i className={`fas ${currentPlan === 'basic_paid' ? 'fa-crown' : 'fa-user'} text-white text-2xl`}></i>
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  {plans[currentPlan].name}
                </h2>
                <p className="text-slate-600">
                  {currentPlan === 'basic_paid' 
                    ? `£${userData.amount}/month • Next billing: ${userData.nextBilling}` 
                    : 'Free forever • No billing required'
                  }
                </p>
                <p className="text-sm text-slate-500">
                  Active since {userData.planSince}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-slate-900">
                  {userData.eventsUsed}
                </div>
                <div className="text-sm text-slate-600">Events used</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-slate-900">
                  {userData.eventsLimit === 999 ? '∞' : userData.eventsLimit}
                </div>
                <div className="text-sm text-slate-600">Events limit</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-slate-900">
                  {userData.aiUsed}
                </div>
                <div className="text-sm text-slate-600">AI uses</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-slate-900">
                  {userData.aiLimit === 999 ? '∞' : userData.aiLimit}
                </div>
                <div className="text-sm text-slate-600">AI limit</div>
              </div>
            </div>
          </div>

          {/* Usage Progress Bars */}
          {currentPlan === 'free' && (
            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="font-medium text-slate-700">Events this month</span>
                    <span className="text-slate-600">{userData.eventsUsed}/{userData.eventsLimit}</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-blue-600 to-purple-600 h-2 rounded-full transition-all duration-300" 
                      style={{ width: `${getUsagePercentage(userData.eventsUsed, userData.eventsLimit)}%` }}
                    ></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="font-medium text-slate-700">AI uses this month</span>
                    <span className="text-slate-600">{userData.aiUsed}/{userData.aiLimit}</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-purple-600 to-blue-600 h-2 rounded-full transition-all duration-300" 
                      style={{ width: `${getUsagePercentage(userData.aiUsed, userData.aiLimit)}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Pricing Plans */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-12">
          {/* Free Tier */}
          <div className={`bg-white rounded-xl border shadow-sm relative overflow-hidden ${
            currentPlan === 'free' ? 'border-blue-500 ring-2 ring-blue-100' : 'border-slate-200'
          }`}>
            {currentPlan === 'free' && (
              <div className="absolute top-4 right-4">
                <span className="bg-blue-600 text-white text-sm px-3 py-1 rounded-full font-medium">
                  CURRENT PLAN
                </span>
              </div>
            )}
            
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-slate-600 rounded-xl mx-auto mb-4 flex items-center justify-center">
                  <i className="fas fa-user text-white text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">{plans.free.name}</h3>
                <div className="text-4xl font-bold text-slate-900 mb-2">
                  £{plans.free.price}
                  <span className="text-lg text-slate-600">/{plans.free.period}</span>
                </div>
                <p className="text-slate-600">{plans.free.description}</p>
              </div>
              
              <div className="space-y-4 mb-8">
                <h4 className="font-semibold text-slate-900">What's included:</h4>
                <ul className="space-y-3">
                  {plans.free.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <i className="fas fa-check text-green-500 mr-3 mt-1 flex-shrink-0"></i>
                      <span className="text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <h4 className="font-semibold text-slate-900 pt-4">Limitations:</h4>
                <ul className="space-y-2">
                  {plans.free.limitations.map((limitation, index) => (
                    <li key={index} className="flex items-start">
                      <i className="fas fa-times text-red-400 mr-3 mt-1 flex-shrink-0"></i>
                      <span className="text-slate-500 text-sm">{limitation}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              {currentPlan === 'free' ? (
                <div className="text-center text-slate-600 py-3 border border-slate-300 rounded-lg bg-slate-50">
                  <i className="fas fa-check mr-2"></i>
                  Your Current Plan
                </div>
              ) : (
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="w-full bg-slate-600 hover:bg-slate-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                >
                  Downgrade to Free
                </button>
              )}
            </div>
          </div>
          
          {/* Basic Paid Tier */}
          <div className={`bg-white rounded-xl border shadow-sm relative overflow-hidden ${
            currentPlan === 'basic_paid' 
              ? 'border-purple-500 ring-2 ring-purple-100' 
              : 'border-purple-200 bg-gradient-to-br from-purple-50/50 to-blue-50/50'
          }`}>
            <div className="absolute top-4 right-4">
              <span className={`text-sm px-3 py-1 rounded-full font-medium ${
                currentPlan === 'basic_paid' 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
              }`}>
                {currentPlan === 'basic_paid' ? 'CURRENT PLAN' : 'POPULAR'}
              </span>
            </div>
            
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl mx-auto mb-4 flex items-center justify-center">
                  <i className="fas fa-crown text-white text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">{plans.basic_paid.name}</h3>
                <div className="text-4xl font-bold text-slate-900 mb-2">
                  £{plans.basic_paid.price}
                  <span className="text-lg text-slate-600">/{plans.basic_paid.period}</span>
                </div>
                <p className="text-slate-700">{plans.basic_paid.description}</p>
              </div>
              
              <div className="space-y-4 mb-8">
                <h4 className="font-semibold text-slate-900">Everything in Free, plus:</h4>
                <ul className="space-y-3">
                  {plans.basic_paid.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <i className="fas fa-check text-green-500 mr-3 mt-1 flex-shrink-0"></i>
                      <span className="text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              {currentPlan === 'basic_paid' ? (
                <div className="text-center text-white py-3 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg">
                  <i className="fas fa-crown mr-2"></i>
                  Your Current Plan
                </div>
              ) : (
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-all transform hover:scale-105"
                >
                  <i className="fas fa-rocket mr-2"></i>
                  Upgrade Now
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Feature Comparison Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-12">
          <div className="p-6 border-b border-slate-200">
            <h2 className="text-xl font-bold text-slate-900 text-center">Feature Comparison</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-4 px-6 text-slate-900 font-medium">Feature</th>
                  <th className="text-center py-4 px-6 text-slate-900 font-medium">Free Tier</th>
                  <th className="text-center py-4 px-6 text-slate-900 font-medium">Basic Paid</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {[
                  ['Event listings per month', '1', 'Unlimited'],
                  ['AI assistant usage', '3 per month', 'Unlimited'],
                  ['RSVP data collection', '❌', '✅'],
                  ['Customer database', '❌', '✅'],
                  ['Email campaigns', '❌', '✅'],
                  ['Featured event badges', '❌', '✅'],
                  ['Event editing & deletion', '❌', '✅'],
                  ['Analytics dashboard', 'Basic', 'Advanced'],
                  ['Priority support', '❌', '✅'],
                  ['Export customer data', '❌', '✅'],
                  ['Social media integration', '❌', '✅'],
                  ['Custom branding', '❌', '✅']
                ].map(([feature, free, paid], index) => (
                  <tr key={index} className="hover:bg-slate-50">
                    <td className="py-4 px-6 text-slate-700">{feature}</td>
                    <td className="py-4 px-6 text-center text-slate-600">{free}</td>
                    <td className="py-4 px-6 text-center text-green-600 font-medium">{paid}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="p-6 border-b border-slate-200">
            <h2 className="text-xl font-bold text-slate-900 text-center">Frequently Asked Questions</h2>
          </div>
          
          <div className="p-6 grid gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Can I cancel my subscription anytime?</h3>
              <p className="text-slate-600">
                Yes, you can cancel your subscription at any time. You'll continue to have access to 
                Basic Paid features until the end of your current billing period.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">What happens to my data if I downgrade?</h3>
              <p className="text-slate-600">
                Your existing events remain visible, but you'll be limited to creating 1 new event 
                per month and lose access to premium features like customer data and email campaigns.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Do you offer refunds?</h3>
              <p className="text-slate-600">
                We offer a 30-day money-back guarantee. If you're not satisfied with Basic Paid 
                within the first month, contact us for a full refund.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">How does billing work?</h3>
              <p className="text-slate-600">
                You'll be charged £20 per month on the same date you upgraded. We accept all major 
                credit cards and process payments securely through Stripe.
              </p>
            </div>
          </div>
        </div>

        {/* Contact Support */}
        <div className="text-center mt-12">
          <p className="text-slate-600 mb-4">
            Have questions about pricing or need help choosing the right plan?
          </p>
          <a 
            href="mailto:support@trentconnect.com" 
            className="text-blue-600 hover:text-blue-700 font-medium transition-colors"
          >
            Contact our support team →
          </a>
        </div>

        {/* Upgrade Modal */}
        {showUpgradeModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
              <div className="p-6 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900">Upgrade to Basic Paid</h3>
              </div>
              
              <div className="p-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl mx-auto mb-4 flex items-center justify-center">
                    <i className="fas fa-crown text-white text-2xl"></i>
                  </div>
                  <h4 className="text-xl font-bold text-slate-900 mb-2">Unlock All Features</h4>
                  <p className="text-slate-600">Get unlimited events, customer management, and email campaigns</p>
                </div>

                <div className="bg-slate-50 rounded-lg p-4 mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-slate-700">Basic Paid Plan</span>
                    <span className="font-semibold text-slate-900">£20.00/month</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-700">First month</span>
                    <span className="font-semibold text-green-600">FREE</span>
                  </div>
                  <div className="border-t border-slate-200 mt-3 pt-3">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-slate-900">Next billing</span>
                      <span className="font-semibold text-slate-900">March 20, 2025</span>
                    </div>
                  </div>
                </div>

                <div className="text-sm text-slate-600 mb-6">
                  <i className="fas fa-info-circle mr-2"></i>
                  Cancel anytime. 30-day money-back guarantee.
                </div>
              </div>
              
              <div className="p-6 border-t border-slate-200 flex space-x-3">
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="flex-1 px-4 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpgrade}
                  disabled={isUpgrading}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:from-slate-400 disabled:to-slate-400 text-white px-4 py-3 rounded-lg transition-all disabled:cursor-not-allowed"
                >
                  {isUpgrading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline-block mr-2"></div>
                      Upgrading...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-rocket mr-2"></i>
                      Start Free Trial
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Cancel Modal */}
        {showCancelModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
              <div className="p-6 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900">Cancel Subscription</h3>
              </div>
              
              <div className="p-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-red-100 rounded-xl mx-auto mb-4 flex items-center justify-center">
                    <i className="fas fa-exclamation-triangle text-red-600 text-2xl"></i>
                  </div>
                  <h4 className="text-xl font-bold text-slate-900 mb-2">Are you sure?</h4>
                  <p className="text-slate-600">You'll lose access to premium features but keep them until February 20, 2025</p>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <h5 className="font-semibold text-red-900 mb-2">You'll lose access to:</h5>
                  <ul className="text-sm text-red-800 space-y-1">
                    <li>• Unlimited events (back to 1/month)</li>
                    <li>• Customer database and email campaigns</li>
                    <li>• Advanced analytics</li>
                    <li>• Priority support</li>
                  </ul>
                </div>
              </div>
              
              <div className="p-6 border-t border-slate-200 flex space-x-3">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="flex-1 bg-slate-600 hover:bg-slate-700 text-white px-4 py-3 rounded-lg transition-colors"
                >
                  Keep Subscription
                </button>
                <button
                  onClick={handleCancel}
                  disabled={isCancelling}
                  className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-slate-400 text-white px-4 py-3 rounded-lg transition-colors disabled:cursor-not-allowed"
                >
                  {isCancelling ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline-block mr-2"></div>
                      Cancelling...
                    </>
                  ) : (
                    'Cancel Subscription'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}